import { createElement, useState } from "react";
import { ClipboardIcon, CheckIcon } from "@heroicons/react/24/outline";
import { AdminLayout } from "../../../layouts/admin-layout";

interface DeploySummary {
  id: number;
  project_id: number | null;
  deploy_type: string | null;
  status: string | null;
  created_at: string | null;
  finished_at: string | null;
}

interface TraceEntry {
  id: number;
  time: string;
  method: string;
  full_method: string;
  is_fault: boolean;
  duration_ms: number | null;
  request_id: string | null;
  customer_id: string | null;
  campaign_id: number | null;
  ad_group_id: number | null;
  ad_id: number | null;
  budget_id: number | null;
  keyword_id: number | null;
  caller_method: string | null;
  request_summary: string[];
  request_payload: unknown;
  response_payload: unknown;
  error_payload: unknown;
  error_message: string | null;
}

interface TraceData {
  deploy_id: number;
  deploy: DeploySummary | null;
  call_count: number;
  fault_count: number;
  entries: TraceEntry[];
}

interface ShowProps {
  deploy_id: number;
  trace_text: string;
  trace: TraceData;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      console.error("Copy failed", e);
    }
  };

  return (
    <button
      type="button"
      onClick={onCopy}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-border bg-card hover:bg-muted text-foreground"
    >
      {copied ? (
        <>
          <CheckIcon className="w-4 h-4 text-green-600" />
          Copied
        </>
      ) : (
        <>
          <ClipboardIcon className="w-4 h-4" />
          Copy
        </>
      )}
    </button>
  );
}

function CallRow({ entry }: { entry: TraceEntry }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={`border-l-4 px-3 py-2 mb-1 rounded-r ${
        entry.is_fault ? "border-red-500 bg-red-50" : "border-border bg-card"
      }`}
    >
      <button
        type="button"
        className="w-full text-left flex items-baseline gap-3 cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="font-mono text-xs text-muted-foreground">
          {entry.time?.split("T")[1]?.split("+")[0] || entry.time}
        </span>
        {entry.is_fault && (
          <span className="text-xs font-semibold text-red-600">FAULT</span>
        )}
        <span className="font-mono text-sm text-foreground">{entry.method}</span>
        <span className="text-xs text-muted-foreground">
          {[
            entry.campaign_id && `campaign=${entry.campaign_id}`,
            entry.ad_group_id && `ad_group=${entry.ad_group_id}`,
            entry.ad_id && `ad=${entry.ad_id}`,
            entry.budget_id && `budget=${entry.budget_id}`,
            entry.keyword_id && `keyword=${entry.keyword_id}`,
            entry.duration_ms && `${entry.duration_ms}ms`,
          ]
            .filter(Boolean)
            .join(" ")}
        </span>
        {entry.caller_method && (
          <span className="font-mono text-xs text-primary/80 ml-auto">
            ← {entry.caller_method.replace(/^GoogleAds::Resources::/, "")}
          </span>
        )}
      </button>

      {entry.request_summary.length > 0 && (
        <div className="mt-1 ml-12 text-xs font-mono text-foreground/80">
          {entry.request_summary.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      )}

      {expanded && (
        <div className="mt-2 ml-12 space-y-2">
          <PayloadBlock label="Request" payload={entry.request_payload} />
          {entry.response_payload != null && (
            <PayloadBlock label="Response" payload={entry.response_payload} />
          )}
          {entry.error_payload != null && (
            <PayloadBlock label="Error" payload={entry.error_payload} />
          )}
        </div>
      )}
    </div>
  );
}

function PayloadBlock({ label, payload }: { label: string; payload: unknown }) {
  const text = JSON.stringify(payload, null, 2);
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs font-semibold text-muted-foreground uppercase">
          {label}
        </span>
        <CopyButton text={text} />
      </div>
      <pre className="text-xs font-mono bg-muted p-2 rounded overflow-x-auto max-h-96">
        {text}
      </pre>
    </div>
  );
}

function GoogleAdsTraceShow({ deploy_id, trace_text, trace }: ShowProps) {
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">
          Google Ads Trace — Deploy #{deploy_id}
        </h1>
        {trace.deploy && (
          <p className="text-sm text-muted-foreground mt-1">
            project={trace.deploy.project_id} · type={trace.deploy.deploy_type} ·
            status={trace.deploy.status} · {trace.call_count} calls,{" "}
            {trace.fault_count} faults
          </p>
        )}
      </header>

      <section className="mb-6 bg-card rounded-lg border border-border p-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-foreground">Plain text trace</h2>
          <CopyButton text={trace_text} />
        </div>
        <pre className="text-xs font-mono bg-muted p-3 rounded overflow-x-auto max-h-[28rem] whitespace-pre">
          {trace_text}
        </pre>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-foreground mb-2">
          Calls (click to expand payloads)
        </h2>
        {trace.entries.length === 0 ? (
          <div className="text-sm text-muted-foreground p-4 bg-card border border-border rounded">
            No Google Ads API calls recorded for this deploy.
          </div>
        ) : (
          <div>
            {trace.entries.map((entry) => (
              <CallRow key={entry.id} entry={entry} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

GoogleAdsTraceShow.layout = (page: React.ReactNode) =>
  createElement(AdminLayout, null, page);

export default GoogleAdsTraceShow;
